import React, { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useLanguage } from "@/context/LanguageContext";
import { Menu, X, Languages } from "lucide-react";
import { Button } from "@/components/ui/button";

const linkBase =
  "text-sm font-medium tracking-wide transition-colors duration-200";

export default function Navbar() {
  const { lang, toggle, t } = useLanguage();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const items = [
    { to: "/", label: t.nav.home, testid: "nav-home" },
    { to: "/services", label: t.nav.services, testid: "nav-services" },
    { to: "/about", label: t.nav.about, testid: "nav-about" },
    { to: "/testimonials", label: t.nav.testimonials, testid: "nav-testimonials" },
    { to: "/contact", label: t.nav.contact, testid: "nav-contact" },
  ];

  return (
    <header
      className="glass-header sticky top-0 z-50 border-b border-brand-line"
      data-testid="site-header"
    >
      <div className="container-pad flex h-20 items-center justify-between gap-4">
        <Link to="/" className="flex items-center gap-3" data-testid="nav-brand">
          <div className="h-12 w-12 rounded-xl bg-brand-primary overflow-hidden flex items-center justify-center shrink-0">
            <img
              src="/logo-dark.png"
              alt="Rodríguez Tax Services logo"
              className="w-full scale-[1.9] origin-top translate-y-1"
            />
          </div>
          <div className="leading-tight">
            <div className="font-serif text-base sm:text-lg text-brand-primary">Rodríguez</div>
            <div className="text-[9px] sm:text-[10px] uppercase tracking-[0.2em] text-brand-muteText">
              Tax Services
            </div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-8" data-testid="primary-nav">
          {items.map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              data-testid={it.testid}
              className={({ isActive }) =>
                `${linkBase} ${
                  isActive
                    ? "text-brand-primary"
                    : "text-brand-text/70 hover:text-brand-primary"
                }`
              }
              end={it.to === "/"}
            >
              {it.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <button
            onClick={toggle}
            data-testid="lang-toggle"
            aria-label={lang === "es" ? "Switch to English" : "Cambiar a español"}
            className="flex items-center gap-1.5 rounded-full border border-brand-line px-3 py-1.5 text-xs font-medium text-brand-primary hover:bg-brand-subtle transition-colors"
          >
            <Languages size={14} />
            <span>{t.lang_toggle}</span>
          </button>
          <Button
            onClick={() => navigate("/contact")}
            data-testid="nav-cta-contact"
            className="rounded-full bg-brand-primary text-brand-surface hover:bg-brand-secondary px-5"
          >
            {t.nav.cta}
          </Button>
        </div>

        <button
          className="lg:hidden text-brand-primary"
          onClick={() => setOpen(!open)}
          data-testid="mobile-menu-btn"
          aria-label="Menu"
        >
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden border-t border-brand-line bg-brand-bg" data-testid="mobile-menu">
          <div className="container-pad py-6 flex flex-col gap-4">
            {items.map((it) => (
              <NavLink
                key={it.to}
                to={it.to}
                onClick={() => setOpen(false)}
                data-testid={`m-${it.testid}`}
                className={({ isActive }) =>
                  `text-base font-medium ${
                    isActive ? "text-brand-primary" : "text-brand-text/80"
                  }`
                }
                end={it.to === "/"}
              >
                {it.label}
              </NavLink>
            ))}
            <div className="flex items-center gap-3 pt-2">
              <button
                onClick={toggle}
                data-testid="m-lang-toggle"
                className="flex items-center gap-1.5 rounded-full border border-brand-line px-3 py-1.5 text-xs font-medium text-brand-primary"
              >
                <Languages size={14} />
                <span>{t.lang_toggle}</span>
              </button>
              <Button
                onClick={() => {
                  setOpen(false);
                  navigate("/contact");
                }}
                data-testid="m-nav-cta-contact"
                className="rounded-full bg-brand-primary text-brand-surface hover:bg-brand-secondary"
              >
                {t.nav.cta}
              </Button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
